import type { CmmdProviderAccountInventoryAccount, ProviderKind } from "@cmmd-forge/contracts";
import { ChevronDownIcon } from "lucide-react";

import { findProviderAccountByIdentity } from "@cmmd-forge/shared/providerAccountIdentity";
import { cn } from "~/lib/utils";
import { useProviderUsage } from "../../usage/useProviderUsage";
import {
  AccountAvatar,
  AccountFlyout,
  PlanBadge,
  STRIP_ROW_CLASS,
  accountKey,
  accountLabel,
  remainingPercentFor,
} from "./ProviderAccountFlyout";

/**
 * What the strip says when nothing healthy resolves.
 *
 * There is deliberately no silent substitution: a turn must never run on an
 * account the user did not pick. The line still has to be actionable, so it
 * names the account that needs reconnecting whenever one is identifiable.
 */
function stripStatusLabel(input: {
  readonly reconnectAccount: CmmdProviderAccountInventoryAccount | undefined;
  readonly hasAccounts: boolean;
  readonly savedAccountId: string | undefined;
  readonly hasAlternatives: boolean;
}): string {
  if (input.reconnectAccount !== undefined) {
    return `${accountLabel(input.reconnectAccount)} · Reconnect required`;
  }
  if (input.savedAccountId === undefined && input.hasAccounts) return "Reconnect required";
  return input.hasAlternatives ? "Selected account unavailable" : "Reconnect in provider settings";
}

/** The strip's row content: avatar, name/status, remaining percent, plan
 * badge, and the folded-in inventory age. Shared by both the disabled
 * (no-alternatives) row and the popover trigger. */
function StripContent(props: {
  readonly selectedAccount: CmmdProviderAccountInventoryAccount | undefined;
  readonly statusLabel: string;
  readonly hasAlternatives: boolean;
  readonly remaining: number | null;
  readonly ageSuffix: string | null;
}) {
  return (
    <>
      <AccountAvatar
        name={props.selectedAccount ? accountLabel(props.selectedAccount) : "?"}
        remaining={props.remaining}
        size={18}
      />
      <span className="min-w-0 flex-1 truncate">
        <span className="font-medium text-foreground">
          {props.selectedAccount ? accountLabel(props.selectedAccount) : props.statusLabel}
        </span>
        {props.remaining !== null ? ` · ${props.remaining}% left` : null}
        {props.ageSuffix}
      </span>
      {props.selectedAccount ? (
        <PlanBadge className="ml-auto" planType={props.selectedAccount.planType} />
      ) : null}
      {props.hasAlternatives ? (
        <ChevronDownIcon aria-hidden="true" className="size-3 shrink-0" />
      ) : null}
    </>
  );
}

function useAccountPickerState(props: {
  readonly accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly provider: ProviderKind;
  readonly selectedAccountId: string | undefined;
}) {
  const usage = useProviderUsage();
  const accounts = props.accounts.filter((account) => account.provider === props.provider);
  // The header only ever resolves to an account a turn could actually run on,
  // so an unhealthy saved selection reports that instead of quietly standing
  // in for the account the user picked. The list below it is the whole set,
  // health and all. `hasAlternatives` counts every row the same way, which is
  // what keeps the flyout reachable once an account goes unhealthy.
  const healthyAccounts = accounts.filter((account) => !account.needsReconnect);
  const savedAccount = props.selectedAccountId
    ? findProviderAccountByIdentity(accounts, props.selectedAccountId)
    : undefined;
  const selectedAccount = props.selectedAccountId
    ? savedAccount?.needsReconnect
      ? undefined
      : savedAccount
    : (healthyAccounts.find((account) => account.isDefault) ?? healthyAccounts[0]);
  const usageByAccountId = new Map(
    usage.accounts.map(({ account, snapshot }) => [accountKey(account), snapshot] as const),
  );
  return {
    accounts,
    selectedAccount,
    reconnectAccount: savedAccount?.needsReconnect ? savedAccount : undefined,
    usageByAccountId,
    hasAlternatives: accounts.length > (selectedAccount ? 1 : 0),
  };
}

/**
 * Compact, always-one-line strip: the active account's avatar (with a usage
 * ring), name, remaining percent, and plan badge, with the last-known
 * inventory age folded in after it when there is one. Takes the place of
 * the plain "Models as of…" text above the model search box — see
 * ModelPickerContent, which renders this instead of ModelPickerInventoryAge
 * whenever accounts are available.
 */
export function ProviderAccountPicker(props: {
  readonly accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly provider: ProviderKind;
  readonly selectedAccountId: string | undefined;
  readonly onChange: (provider: "codex" | "claudeAgent", accountId: string) => void;
  readonly inventoryAgeLabel?: string | null;
}) {
  const { accounts, selectedAccount, reconnectAccount, usageByAccountId, hasAlternatives } =
    useAccountPickerState(props);
  // Only a provider with nothing at all to say stays silent. Keying this off
  // the resolved account instead hid the whole strip whenever the one account
  // on file needed reconnecting, because a fresh thread carries no saved id
  // (composerModelState.ts, resolveProviderAccountId).
  if (accounts.length === 0 && !props.selectedAccountId) return null;

  const remaining = remainingPercentFor(
    selectedAccount ? (usageByAccountId.get(accountKey(selectedAccount)) ?? null) : null,
  );
  const ageSuffix = props.inventoryAgeLabel
    ? ` · ${props.inventoryAgeLabel.replace(/^Models as of /, "")}`
    : null;
  const content = (
    <StripContent
      ageSuffix={ageSuffix}
      hasAlternatives={hasAlternatives}
      remaining={remaining}
      selectedAccount={selectedAccount}
      statusLabel={stripStatusLabel({
        reconnectAccount,
        hasAccounts: accounts.length > 0,
        savedAccountId: props.selectedAccountId,
        hasAlternatives,
      })}
    />
  );

  return (
    <div className="border-b" data-testid="provider-account-strip">
      {hasAlternatives ? (
        <AccountFlyout
          accounts={accounts}
          onSelect={(account) => props.onChange(account.provider, account.providerAccountId)}
          selectedAccount={selectedAccount}
          usageByAccountId={usageByAccountId}
        >
          {content}
        </AccountFlyout>
      ) : (
        <button type="button" className={cn(STRIP_ROW_CLASS, "cursor-default")} disabled>
          {content}
        </button>
      )}
    </div>
  );
}
